import {
  Component,
  Input,
  forwardRef,
  ChangeDetectorRef,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OnlyNumbers } from 'projects/shared/validations/onlyNumbers';
import { OnlyLetters } from 'projects/shared/validations/onlyLetters';

export type LangKey = 'en' | 'ar';

export interface LangInputValue {
  en: string;
  ar: string;
}

export type SingleValue = string | number | null;

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: [FormsModule, CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = '';

  // Validations
  @Input() validation: 'all' | 'number' | 'text' = 'all';

  // for make a input accepts AR&EN
  @Input() bilingual = false;

  @Input() placeholder = '';

  @Input() placeholderEn = '';
  @Input() placeholderAr = '';

  /** Is the field required (adds the red * like in the design) */
  @Input() required = false;

  @Input() defaultLang: LangKey = 'en';

  @Input() type: 'text' | 'email' | 'number' | 'textarea' = 'text';

  @Input() rows = 5;

  activeLang: LangKey = this.defaultLang;

  singleValue: SingleValue = '';

  value: LangInputValue = { en: '', ar: '' };

  disabled = false;

  inputValidation: Function = (event: any) => event;

  private onChange: (val: SingleValue | LangInputValue) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.setValidation();
  }

  ngOnInit(): void {
    this.activeLang = this.defaultLang;
    this.setValidation();
  }

  setValidation() {
    if (this.validation == 'all') {
      this.inputValidation = (event: any) => event;
    } else if (this.validation == 'number') {
      this.inputValidation = (event: any) => OnlyNumbers(event, true);
    } else if (this.validation == 'text') {
      this.inputValidation = (event: any) => OnlyLetters(event);
    }
  }

  switchTab(lang: LangKey): void {
    if (this.disabled) return;
    this.activeLang = lang;
  }

  onSingleInputChange(val: string | number | null): void {
    this.singleValue = val;
    this.onChange(this.parseValue(val));
    this.onTouched();
  }

  onLangInputChange(lang: LangKey, val: string): void {
    this.value = { ...this.value, [lang]: val };
    this.onChange(this.value);
    this.onTouched();
  }

  private parseValue(val: string | number | null): SingleValue {
    if (this.type !== 'number') return val;

    if (val === '' || val === null || val === undefined) return null;

    const n = Number(val);
    return isNaN(n) ? null : n;
  }

  get currentPlaceholder(): string {
    if (!this.bilingual) return this.placeholder;
    return this.activeLang === 'en'
      ? this.placeholderEn || 'English Name'
      : this.placeholderAr || 'اسم عربي';
  }

  get currentDir(): 'ltr' | 'rtl' {
    return this.activeLang === 'en' ? 'ltr' : 'rtl';
  }

  // ===== ControlValueAccessor =====
  writeValue(val: SingleValue | LangInputValue): void {
    if (this.bilingual) {
      this.value = (val as LangInputValue) || { en: '', ar: '' };
    } else {
      this.singleValue = (val as SingleValue) ?? '';
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: SingleValue | LangInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}